// Call Management Service for Multi-Participant Video Calls
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.kokkobeo.com";

export enum CallType {
  AUDIO = "audio",
  VIDEO = "video"
}

export interface ActiveCall {
  id: string;
  channelName: string;
  chatId: string;
  initiatedBy: string;
  callType: "audio" | "video";
  status: "waiting" | "active" | "ended";
  maxParticipants: number;
  createdAt: string;
  startedAt?: string;
  endedAt?: string;
  participants: CallParticipant[];
  initiator: {
    id: string;
    name: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface CallParticipant {
  id: string;
  callId: string;
  userId: string;
  agoraUid: number;
  status: "invited" | "joining" | "connected" | "left";
  joinedAt?: string;
  leftAt?: string;
  isAudioEnabled: boolean;
  isVideoEnabled: boolean;
  user: {
    id: string;
    name: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
  };
}

export interface InitiateCallRequest {
  chatId: string;
  initiatedBy: string;
  callType: "audio" | "video";
  channelName: string;
  participantIds: string[];
}

export interface JoinCallRequest {
  userId: string;
  agoraUid: number;
}

export interface LeaveCallRequest {
  userId: string;
  reason?: string;
}

export interface EndCallRequest {
  userId: string;
  reason?: string;
}

export interface ToggleAudioRequest {
  isEnabled: boolean;
}

export interface ToggleVideoRequest {
  isEnabled: boolean;
}

export class CallManagementService {
  /**
   * Initiate a new video call
   */
  static async initiateCall(request: InitiateCallRequest): Promise<ActiveCall> {
    try {
      console.log("📞 Initiating call:", request);
      
      const response = await fetch(`${API_BASE_URL}api/v1/calls/initiate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Add authorization header if needed
          // "Authorization": `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const callData = await response.json();
      console.log("✅ Call initiated:", callData);
      return callData;
    } catch (error) {
      console.error("❌ Failed to initiate call:", error);
      throw error;
    }
  }

  /**
   * Join an existing call
   */
  static async joinCall(callId: string, request: JoinCallRequest): Promise<CallParticipant> {
    try {
      console.log("🚀 Joining call:", { callId, ...request });
      
      const response = await fetch(`${API_BASE_URL}api/v1/calls/${callId}/join`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Add authorization header if needed
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const participantData = await response.json();
      console.log("✅ Joined call:", participantData);
      return participantData;
    } catch (error) {
      console.error("❌ Failed to join call:", error);
      throw error;
    }
  }

  /**
   * Leave a call
   */
  static async leaveCall(callId: string, request: LeaveCallRequest): Promise<void> {
    try {
      console.log("👋 Leaving call:", { callId, ...request });
      
      const response = await fetch(`${API_BASE_URL}api/v1/calls/${callId}/leave`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      console.log("✅ Left call successfully");
    } catch (error) {
      console.error("❌ Failed to leave call:", error);
      throw error;
    }
  }

  /**
   * End a call (only initiator can do this)
   */
  static async endCall(callId: string, request: EndCallRequest): Promise<void> {
    try {
      console.log("🔚 Ending call:", { callId, ...request });
      
      const response = await fetch(`${API_BASE_URL}api/v1/calls/${callId}/end`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      console.log("✅ Call ended successfully");
    } catch (error) {
      console.error("❌ Failed to end call:", error);
      throw error;
    }
  }

  /**
   * Get active calls for a chat
   */
  static async getActiveCallsForChat(chatId: string): Promise<ActiveCall[]> {
    try {
      const response = await fetch(`${API_BASE_URL}api/v1/calls/chat/${chatId}/active`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const calls = await response.json();
      console.log("📋 Active calls for chat:", calls);
      return calls;
    } catch (error) {
      console.error("❌ Failed to get active calls:", error);
      throw error;
    }
  }

  /**
   * Get participants of a call
   */
  static async getCallParticipants(callId: string): Promise<CallParticipant[]> {
    try {
      const response = await fetch(`${API_BASE_URL}api/v1/calls/${callId}/participants`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const participants = await response.json();
      return participants;
    } catch (error) {
      console.error("❌ Failed to get call participants:", error);
      throw error;
    }
  }

  /**
   * Toggle participant audio
   */
  static async toggleParticipantAudio(
    callId: string, 
    userId: string, 
    request: ToggleAudioRequest
  ): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}api/v1/calls/${callId}/participants/${userId}/audio`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      console.log("🎤 Audio toggled:", request.isEnabled ? "enabled" : "disabled");
    } catch (error) {
      console.error("❌ Failed to toggle audio:", error);
      throw error;
    }
  }

  /**
   * Toggle participant video
   */
  static async toggleParticipantVideo(
    callId: string, 
    userId: string, 
    request: ToggleVideoRequest
  ): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}api/v1/calls/${callId}/participants/${userId}/video`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      console.log("📹 Video toggled:", request.isEnabled ? "enabled" : "disabled");
    } catch (error) {
      console.error("❌ Failed to toggle video:", error);
      throw error;
    }
  }

  /**
   * Generate a unique channel name for a chat
   */
  static generateChannelName(chatId: string): string {
    const timestamp = Date.now();
    return `chat_${chatId}_${timestamp}`;
  }

  /**
   * Generate a random Agora UID
   */
  static generateAgoraUid(): number {
    return Math.floor(Math.random() * 1000000) + 1;
  }
}
